export class ChatView {
	constructor(state, options = {}) {
		this.state = state;
		this.options = options;
		this.element = null;
		this._unsubscribe = null;
		this._typingTimeout = null;
		this._isTyping = false;
		this._typingIndicator = null;
		this._isConversationClosed = false;
		this._pendingAttachments = [];
		this._emojiPickerOpen = false;
		this._emojiOutsideHandler = null;
		this._menuOutsideHandler = null;
	}

	render() {
		this.element = document.createElement('div');
		this.element.className = 'liveChat-view liveChat-chat-view';

		this._updateContent();

		this._unsubscribe = this.state.subscribe((type, data) => {
			if (type === 'connectionChange') {
				const banner = this.element?.querySelector(
					'.liveChat-connection-banner'
				);
				if (banner) {
					banner.style.display = data.connected ? 'none' : 'flex';
				}
				return;
			}
			if (
				type === 'messageAdded' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._hideTypingIndicator();
				this._appendMessage(data.message);
			} else if (
				type === 'typingStarted' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._showTypingIndicator(data.userName);
			} else if (
				type === 'typingStopped' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._hideTypingIndicator();
			} else if (
				type === 'conversationUpdated' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._updateContent();
			} else if (
				type === 'messagesUpdate' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._updateContent();
			} else if (type === 'settingsUpdate') {
				this._updateContent();
			}
		});

		return this.element;
	}

	_updateContent() {
		const conversation = this.state.getActiveConversation();
		const messages = this.state.getActiveMessages();
		const isNewConversation = !this.state.activeConversationId;
		const isClosed = !isNewConversation && conversation?.status === 'closed';
		this._isConversationClosed = isClosed;

		const messagesHtml =
			messages.length === 0
				? this._renderEmptyState(isNewConversation)
				: this._renderGroupedMessages(messages);

		const defaultPlaceholder =
			this.options.composePlaceholder || 'Write a message...';
		const placeholder = isNewConversation
			? this.options.composePlaceholder || 'Start typing your message...'
			: isClosed
				? 'Conversation closed'
				: defaultPlaceholder;

		const logoUrl = this.options.logoUrl;
		const teamName = this.state.teamName || 'Support';
		const headerAvatarHtml = logoUrl
			? `<img src="${this._escapeHtml(logoUrl)}" alt="${this._escapeHtml(teamName)}" />`
			: `<iconify-icon icon="ph:chats-circle" width="20" height="20"></iconify-icon>`;

		const isExpanded = this.options.isExpanded?.() ?? false;
		const expandIcon = isExpanded ? 'ph:arrows-in' : 'ph:arrows-out';
		const expandLabel = isExpanded ? 'Collapse window' : 'Expand window';

		this.element.innerHTML = `
			<div class="liveChat-chat-header">
				<button class="sdk-btn-icon liveChat-back-btn" aria-label="Back">
					<iconify-icon icon="ph:arrow-left" width="20" height="20"></iconify-icon>
				</button>
				<div class="liveChat-chat-header-avatar">
					${headerAvatarHtml}
				</div>
				<div class="liveChat-chat-header-info">
					<span class="liveChat-chat-title">${this._escapeHtml(teamName)}</span>
					${isClosed || this.state.showReplyTime !== false ? `<span class="liveChat-chat-subtitle">${isClosed ? 'Conversation resolved' : this.state.responseTime || 'Typically replies within minutes'}</span>` : ''}
				</div>
				<div class="liveChat-chat-header-actions">
					<div class="liveChat-chat-menu-wrapper">
						<button class="sdk-btn-icon liveChat-chat-menu-btn" aria-label="Options">
							<iconify-icon icon="ph:dots-three" width="20" height="20"></iconify-icon>
						</button>
						<div class="liveChat-chat-menu-dropdown">
							<button class="liveChat-chat-menu-item liveChat-expand-window-btn">
								<iconify-icon icon="${expandIcon}" width="16" height="16"></iconify-icon>
								<span>${expandLabel}</span>
							</button>
						</div>
					</div>
					<button class="sdk-btn-icon sdk-close-btn liveChat-mobile-close-btn" aria-label="Close">
						<iconify-icon icon="ph:x" width="18" height="18"></iconify-icon>
					</button>
				</div>
			</div>

			<div class="liveChat-connection-banner" style="display:none;">
				<iconify-icon icon="ph:wifi-slash" width="14" height="14"></iconify-icon>
				<span>Reconnecting…</span>
			</div>

			<div class="liveChat-chat-messages">
				${messagesHtml}
				${
					isClosed
						? `
				<div class="liveChat-closed-banner">
					<iconify-icon icon="ph:check-circle" width="18" height="18"></iconify-icon>
					<span>This conversation has been resolved</span>
				</div>
				`
						: ''
				}
				<div class="liveChat-typing-indicator">
					<div class="liveChat-typing-dots">
						<span></span><span></span><span></span>
					</div>
					<span class="liveChat-typing-text"></span>
				</div>
			</div>

			<div class="liveChat-scroll-pill" style="display:none;">
				<iconify-icon icon="ph:arrow-down" width="14" height="14"></iconify-icon>
				<span>New message</span>
			</div>

			${
				isClosed
					? ''
					: `
			<div class="liveChat-compose-attachments-preview"></div>

			<div class="liveChat-chat-compose">
				<div class="liveChat-compose-input-wrapper">
					<textarea class="liveChat-compose-input" placeholder="${placeholder}" rows="1"></textarea>
				</div>
				<div class="liveChat-compose-bottom">
					<div class="liveChat-compose-actions">
						${this.state.allowAttachments !== false ? `<button class="sdk-btn-icon liveChat-compose-attach" aria-label="Attach file"><iconify-icon icon="ph:paperclip" width="20" height="20"></iconify-icon></button>` : ''}
						${this.state.allowEmoji !== false ? `<button class="sdk-btn-icon liveChat-emoji-btn" aria-label="Emoji"><iconify-icon icon="ph:smiley" width="20" height="20"></iconify-icon></button>` : ''}
					</div>
					<button class="liveChat-compose-send" aria-label="Send" disabled>
						<iconify-icon icon="ph:paper-plane-right" width="20" height="20"></iconify-icon>
					</button>
				</div>
				${this.state.allowAttachments !== false ? `<input type="file" class="liveChat-compose-file-input" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" />` : ''}
			</div>
			`
			}
		`;

		this._typingIndicator = this.element.querySelector(
			'.liveChat-typing-indicator'
		);
		this._attachEvents();
		this._scrollToBottom();
		this._renderAttachmentPreviews();
		this._setupScrollPill();
	}

	_renderEmptyState(isNewConversation = false) {
		const logoUrl = this.options.logoUrl;

		const logoHtml = logoUrl
			? `<div class="liveChat-chat-empty-logo"><img src="${this._escapeHtml(logoUrl)}" alt="${this._escapeHtml(this.state.teamName)}" /></div>`
			: '';

		return `
			<div class="liveChat-chat-empty">
				${logoHtml}
				<h3>${isNewConversation ? 'Start a new conversation' : 'Start the conversation'}</h3>
			</div>
		`;
	}

	_renderMessageAttachments(attachments) {
		if (!attachments || attachments.length === 0) return '';
		return attachments
			.map((att) => {
				if (att.type === 'image') {
					return `<img class="liveChat-message-image" src="${this._escapeHtml(att.url)}" alt="${this._escapeHtml(att.name || 'image')}" data-url="${this._escapeHtml(att.url)}" />`;
				}
				return `<a class="liveChat-message-file" href="${this._escapeHtml(att.url)}" data-url="${this._escapeHtml(att.url)}" data-name="${this._escapeHtml(att.name || 'file')}">
				<iconify-icon icon="ph:file" width="18" height="18"></iconify-icon>
				<span>${this._escapeHtml(att.name || 'file')}</span>
				<iconify-icon icon="ph:download-simple" width="16" height="16" class="liveChat-file-download-icon"></iconify-icon>
			</a>`;
			})
			.join('');
	}

	_renderMessage(message, isLastInGroup = true) {
		if (message.isSystem) {
			return this._renderSystemMessage(message);
		}

		const isOwn = message.isOwn;
		const messageClass = isOwn
			? 'liveChat-message-own'
			: 'liveChat-message-received';
		const timeStr = isLastInGroup
			? this._formatMessageTime(message.timestamp)
			: '';
		const attachmentsHtml = this._renderMessageAttachments(message.attachments);
		const isOptimistic = message.isOptimistic;

		const contentHtml = message.content
			? `<div class="liveChat-message-content">${this._formatMessageContent(message.content)}</div>`
			: '';
		const bubbleHtml = contentHtml
			? `<div class="liveChat-message-bubble">${contentHtml}</div>`
			: '';

		if (isOwn) {
			const sentIndicator = isLastInGroup
				? `<div class="liveChat-message-meta liveChat-message-meta-own">
						${
							isOptimistic
								? `<span class="liveChat-message-sent-status">Sending…</span>`
								: `<span class="liveChat-message-sent-status">Sent</span>`
						}
						${timeStr ? `<span>·</span><span>${timeStr}</span>` : ''}
					</div>`
				: '';
			return `
				<div class="liveChat-message ${messageClass}${isOptimistic ? ' liveChat-message-optimistic' : ''}">
					${bubbleHtml}
					${attachmentsHtml}
					${sentIndicator}
				</div>
			`;
		}

		const avatarHtml = this._renderSenderAvatar(message.sender);
		return `
			<div class="liveChat-message ${messageClass}">
				<div class="liveChat-message-row">
					<div class="liveChat-message-avatar">${avatarHtml}</div>
					<div class="liveChat-message-wrapper">
						${bubbleHtml}
						${attachmentsHtml}
					</div>
				</div>
				${timeStr ? `<div class="liveChat-message-meta"><span>${timeStr}</span></div>` : ''}
			</div>
		`;
	}

	_renderSystemMessage(message) {
		const content = message.content || '';
		const isJoinLeave =
			content.includes('joined the chat') ||
			content.includes('left the chat') ||
			content.includes('joined the conversation') ||
			content.includes('left the conversation');

		if (isJoinLeave && message.sender) {
			const rawName = message.sender.name || '';
			const name = rawName
				.split(' ')
				.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
				.join(' ');
			const logoUrl = this.options.logoUrl;
			const logoHtml = logoUrl
				? `<img src="${this._escapeHtml(logoUrl)}" alt="logo" />`
				: name.charAt(0).toUpperCase();
			const timeStr = this._formatMessageTime(message.timestamp);

			const rawAction = content.replace(rawName, '').trim();
			const action = rawAction.charAt(0).toUpperCase() + rawAction.slice(1);

			return `
				<div class="liveChat-message-system-event">
					<div class="liveChat-message-system-event-avatar">${logoHtml}</div>
					<span class="liveChat-message-system-event-name">${this._escapeHtml(name)}</span>
					<span class="liveChat-message-system-event-action">${this._escapeHtml(action)}</span>
					${timeStr ? `<span class="liveChat-message-system-event-time">${timeStr}</span>` : ''}
				</div>
			`;
		}

		// Render automated/bot messages as a proper received chat bubble
		const teamName = this.state.teamName || 'Support';
		const logoUrl = this.options.logoUrl;
		const avatarHtml = logoUrl
			? `<div class="sdk-avatar sdk-avatar-sm"><img src="${this._escapeHtml(logoUrl)}" alt="${this._escapeHtml(teamName)}" /></div>`
			: `<div class="sdk-avatar sdk-avatar-sm">${teamName.charAt(0).toUpperCase()}</div>`;
		const timeStr = this._formatMessageTime(message.timestamp);

		return `
			<div class="liveChat-message liveChat-message-received">
				<div class="liveChat-message-row">
					<div class="liveChat-message-avatar">${avatarHtml}</div>
					<div class="liveChat-message-wrapper">
						<div class="liveChat-message-bubble">
							<div class="liveChat-message-content">${this._formatMessageContent(content)}</div>
						</div>
					</div>
				</div>
				${timeStr ? `<div class="liveChat-message-meta"><span>${timeStr}</span></div>` : ''}
			</div>
		`;
	}

	_renderSenderAvatar(sender) {
		if (sender?.avatarUrl) {
			return `<div class="sdk-avatar sdk-avatar-sm"><img src="${sender.avatarUrl}" alt="${sender.name}" /></div>`;
		}
		const initial = (sender?.name || 'S').charAt(0).toUpperCase();
		return `<div class="sdk-avatar sdk-avatar-sm">${initial}</div>`;
	}

	_renderTeamAvatars() {
		const avatars = this.state.teamAvatars;
		if (!avatars || avatars.length === 0) {
			return '';
		}

		const avatarItems = avatars
			.slice(0, 3)
			.map((avatar) => {
				if (typeof avatar === 'string' && avatar.startsWith('http')) {
					return `<div class="sdk-avatar sdk-avatar-md"><img src="${avatar}" alt="Team member" /></div>`;
				}
				return `<div class="sdk-avatar sdk-avatar-md">${avatar.charAt(0).toUpperCase()}</div>`;
			})
			.join('');

		return `<div class="liveChat-avatar-stack">${avatarItems}</div>`;
	}

	_formatMessageTime(timestamp) {
		if (!timestamp) return '';
		const date = new Date(timestamp);
		const datePart = date.toLocaleDateString('en-GB', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
		});
		const timePart = date.toLocaleTimeString('en-GB', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		});
		return `${datePart}, ${timePart}`;
	}

	_formatMessageContent(content) {
		if (!content) return '';
		return content
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/\n/g, '<br>');
	}

	_appendMessage(message) {
		const messagesContainer = this.element.querySelector(
			'.liveChat-chat-messages'
		);
		const emptyState = messagesContainer.querySelector('.liveChat-chat-empty');
		if (emptyState) {
			emptyState.remove();
		}

		const messageHtml = this._renderMessage(message);
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = messageHtml;
		messagesContainer.appendChild(tempDiv.firstElementChild);

		// Show scroll pill if user is scrolled up, otherwise scroll to bottom
		const isNearBottom =
			messagesContainer.scrollHeight -
				messagesContainer.scrollTop -
				messagesContainer.clientHeight <
			80;

		if (isNearBottom) {
			this._scrollToBottom();
		} else if (!message.isOwn) {
			const pill = this.element.querySelector('.liveChat-scroll-pill');
			if (pill) pill.style.display = 'flex';
		}
	}

	_scrollToBottom() {
		const messagesContainer = this.element.querySelector(
			'.liveChat-chat-messages'
		);
		if (messagesContainer) {
			setTimeout(() => {
				messagesContainer.scrollTop = messagesContainer.scrollHeight;
			}, 50);
		}
	}

	_setupScrollPill() {
		const messagesContainer = this.element.querySelector(
			'.liveChat-chat-messages'
		);
		const pill = this.element.querySelector('.liveChat-scroll-pill');
		if (!messagesContainer || !pill) return;

		pill.addEventListener('click', () => {
			this._scrollToBottom();
			pill.style.display = 'none';
		});

		messagesContainer.addEventListener('scroll', () => {
			const isNearBottom =
				messagesContainer.scrollHeight -
					messagesContainer.scrollTop -
					messagesContainer.clientHeight <
				80;
			if (isNearBottom) {
				pill.style.display = 'none';
			}
		});
	}

	_renderGroupedMessages(messages) {
		const GROUP_TIME_GAP = 5 * 60 * 1000; // 5 minutes
		let html = '';

		messages.forEach((msg, index) => {
			const isLast = index === messages.length - 1;
			const nextMsg = messages[index + 1];
			const currentTime = msg.timestamp ? new Date(msg.timestamp).getTime() : 0;
			const nextTime = nextMsg?.timestamp
				? new Date(nextMsg.timestamp).getTime()
				: null;
			const senderKey = msg.isSystem
				? 'system'
				: msg.isOwn
					? 'own'
					: msg.sender?.name || 'agent';
			const nextSenderKey = nextMsg
				? nextMsg.isSystem
					? 'system'
					: nextMsg.isOwn
						? 'own'
						: nextMsg.sender?.name || 'agent'
				: null;

			const timeGapAfter = nextTime && nextTime - currentTime > GROUP_TIME_GAP;
			const senderChanges = nextMsg && senderKey !== nextSenderKey;
			const isLastInGroup = isLast || timeGapAfter || senderChanges;

			html += this._renderMessage(msg, isLastInGroup);
		});

		return html;
	}

	_updateSendButtonState() {
		const input = this.element.querySelector('.liveChat-compose-input');
		const sendBtn = this.element.querySelector('.liveChat-compose-send');
		if (input && sendBtn) {
			sendBtn.disabled =
				!input.value.trim() && this._pendingAttachments.length === 0;
		}
	}

	_renderAttachmentPreviews() {
		const container = this.element.querySelector(
			'.liveChat-compose-attachments-preview'
		);
		if (!container) return;

		if (this._pendingAttachments.length === 0) {
			container.innerHTML = '';
			container.style.display = 'none';
			return;
		}

		container.style.display = 'flex';
		container.innerHTML = this._pendingAttachments
			.map((att, i) => {
				const isImage = att.type.startsWith('image');
				const thumb = isImage
					? `<img class="liveChat-attachment-thumb" src="${att.preview}" alt="${this._escapeHtml(att.file.name)}" />`
					: `<div class="liveChat-attachment-thumb liveChat-attachment-file-icon"><iconify-icon icon="ph:file" width="24" height="24"></iconify-icon></div>`;
				return `
				<div class="liveChat-attachment-preview" data-index="${i}">
					${thumb}
					<button class="liveChat-attachment-remove" data-index="${i}" aria-label="Remove">&times;</button>
				</div>
			`;
			})
			.join('');

		container.querySelectorAll('.liveChat-attachment-remove').forEach((btn) => {
			btn.addEventListener('click', (e) => {
				const idx = parseInt(e.currentTarget.dataset.index, 10);
				this._pendingAttachments.splice(idx, 1);
				this._renderAttachmentPreviews();
				this._updateSendButtonState();
			});
		});
	}

	_attachEvents() {
		this.element
			.querySelector('.liveChat-back-btn')
			.addEventListener('click', () => {
				this.state.setView('messages');
			});

		const mobileCloseBtn = this.element.querySelector(
			'.liveChat-mobile-close-btn'
		);
		if (mobileCloseBtn) {
			mobileCloseBtn.addEventListener('click', () => {
				this.state.setOpen(false);
			});
		}

		const menuBtn = this.element.querySelector('.liveChat-chat-menu-btn');
		const dropdown = this.element.querySelector('.liveChat-chat-menu-dropdown');
		if (menuBtn && dropdown) {
			menuBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				dropdown.classList.toggle('open');
				if (
					dropdown.classList.contains('open') &&
					this._menuOutsideHandler === null
				) {
					this._menuOutsideHandler = (ev) => {
						if (!dropdown.contains(ev.target) && !menuBtn.contains(ev.target)) {
							dropdown.classList.remove('open');
							document.removeEventListener('click', this._menuOutsideHandler);
							this._menuOutsideHandler = null;
						}
					};
					setTimeout(
						() => document.addEventListener('click', this._menuOutsideHandler),
						0
					);
				}
			});
		}

		const expandWindowBtn = this.element.querySelector(
			'.liveChat-expand-window-btn'
		);
		if (expandWindowBtn && dropdown) {
			expandWindowBtn.addEventListener('click', () => {
				dropdown.classList.remove('open');
				if (this._menuOutsideHandler) {
					document.removeEventListener('click', this._menuOutsideHandler);
					this._menuOutsideHandler = null;
				}
				this.options.onToggleExpand?.();
			});
		}

		const input = this.element.querySelector('.liveChat-compose-input');
		const sendBtn = this.element.querySelector('.liveChat-compose-send');

		if (input && sendBtn) {
			input.addEventListener('input', () => {
				input.style.height = 'auto';
				input.style.height = Math.min(input.scrollHeight, 120) + 'px';
				this._updateSendButtonState();

				if (input.value.trim()) {
					this._startTyping();
				}
			});

			input.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' && !e.shiftKey) {
					e.preventDefault();
					this._sendMessage();
				}
			});

			sendBtn.addEventListener('click', () => {
				this._sendMessage();
			});
		}

		const emojiBtn = this.element.querySelector('.liveChat-emoji-btn');
		if (emojiBtn) {
			emojiBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				this._toggleEmojiPicker();
			});
		}

		const attachBtn = this.element.querySelector('.liveChat-compose-attach');
		const fileInput = this.element.querySelector(
			'.liveChat-compose-file-input'
		);

		if (attachBtn && fileInput) {
			attachBtn.addEventListener('click', () => {
				fileInput.click();
			});

			fileInput.addEventListener('change', (e) => {
				const files = e.target.files;
				if (!files) return;
				Array.from(files).forEach((file) => {
					const reader = new FileReader();
					reader.onload = (ev) => {
						this._pendingAttachments.push({
							file,
							preview: ev.target.result,
							type: file.type,
						});
						this._renderAttachmentPreviews();
						this._updateSendButtonState();
					};
					reader.readAsDataURL(file);
				});
				fileInput.value = '';
			});
		}

		const messagesContainer = this.element.querySelector(
			'.liveChat-chat-messages'
		);
		if (messagesContainer) {
			messagesContainer.addEventListener('click', (e) => {
				const fileLink = e.target.closest('.liveChat-message-file');
				if (fileLink) {
					e.preventDefault();
					const url = fileLink.dataset.url;
					const name = fileLink.dataset.name;
					this._downloadFile(url, name);
					return;
				}

				const img = e.target.closest('.liveChat-message-image');
				if (img) {
					const url = img.dataset.url || img.src;
					window.open(url, '_blank');
				}
			});
		}
	}

	async _downloadFile(url, name) {
		try {
			const response = await fetch(url);
			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = blobUrl;
			a.download = name || 'download';
			a.style.display = 'none';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(blobUrl);
		} catch {
			window.open(url, '_blank');
		}
	}

	_escapeHtml(text) {
		if (!text) return '';
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	async _sendMessage() {
		if (this._isConversationClosed) return;

		const input = this.element.querySelector('.liveChat-compose-input');
		const content = input.value.trim();
		const hasAttachments = this._pendingAttachments.length > 0;

		if (!content && !hasAttachments) return;

		this._stopTyping();

		const attachmentsToSend = [...this._pendingAttachments];
		const isNewConversation = !this.state.activeConversationId;

		if (isNewConversation) {
			if (this.options.onStartConversation) {
				this.options.onStartConversation(content, attachmentsToSend);
			}
		} else {
			const message = {
				id: 'msg_' + Date.now(),
				content: content,
				isOwn: true,
				isOptimistic: true,
				timestamp: new Date().toISOString(),
				attachments: attachmentsToSend.map((a) => ({
					url: a.preview,
					type: a.type.startsWith('image') ? 'image' : 'file',
					name: a.file.name,
				})),
			};

			this.state.addMessage(this.state.activeConversationId, message);

			if (this.options.onSendMessage) {
				this.options.onSendMessage(
					this.state.activeConversationId,
					message,
					attachmentsToSend
				);
			}
		}

		input.value = '';
		input.style.height = 'auto';
		this._pendingAttachments = [];
		this._renderAttachmentPreviews();
		this._updateSendButtonState();
	}

	_loadEmojiPicker() {
		if (document.querySelector('#product7-emoji-picker-script')) {
			return Promise.resolve();
		}
		return new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.id = 'product7-emoji-picker-script';
			script.type = 'module';
			script.src =
				'https://cdn.jsdelivr.net/npm/emoji-picker-element@1/index.js';
			script.onload = resolve;
			script.onerror = reject;
			document.head.appendChild(script);
		});
	}

	async _toggleEmojiPicker() {
		const existing = this.element.querySelector(
			'.liveChat-emoji-picker-container'
		);
		if (existing) {
			existing.remove();
			this._emojiPickerOpen = false;
			if (this._emojiOutsideHandler) {
				document.removeEventListener('click', this._emojiOutsideHandler);
				this._emojiOutsideHandler = null;
			}
			return;
		}

		try {
			await this._loadEmojiPicker();
		} catch {
			return;
		}

		const container = document.createElement('div');
		container.className = 'liveChat-emoji-picker-container';

		const picker = document.createElement('emoji-picker');
		container.appendChild(picker);

		const compose = this.element.querySelector('.liveChat-chat-compose');
		compose.parentNode.insertBefore(container, compose);
		this._emojiPickerOpen = true;

		picker.addEventListener('emoji-click', (e) => {
			this._insertEmoji(e.detail.unicode);
			container.remove();
			this._emojiPickerOpen = false;
			if (this._emojiOutsideHandler) {
				document.removeEventListener('click', this._emojiOutsideHandler);
				this._emojiOutsideHandler = null;
			}
		});

		this._emojiOutsideHandler = (e) => {
			if (
				!container.contains(e.target) &&
				!e.target.closest('.liveChat-emoji-btn')
			) {
				container.remove();
				this._emojiPickerOpen = false;
				document.removeEventListener('click', this._emojiOutsideHandler);
				this._emojiOutsideHandler = null;
			}
		};
		setTimeout(
			() => document.addEventListener('click', this._emojiOutsideHandler),
			0
		);
	}

	_insertEmoji(emoji) {
		const input = this.element.querySelector('.liveChat-compose-input');
		if (!input) return;
		const start = input.selectionStart;
		const end = input.selectionEnd;
		input.value = input.value.slice(0, start) + emoji + input.value.slice(end);
		input.selectionStart = input.selectionEnd = start + emoji.length;
		input.focus();
		input.dispatchEvent(new Event('input'));
	}

	_startTyping() {
		if (this._isConversationClosed) return;
		if (!this._isTyping && this.state.activeConversationId) {
			this._isTyping = true;
			if (this.options.onTyping) {
				this.options.onTyping(this.state.activeConversationId, true);
			}
		}

		if (this._typingTimeout) {
			clearTimeout(this._typingTimeout);
		}
		this._typingTimeout = setTimeout(() => {
			this._stopTyping();
		}, 3000);
	}

	_stopTyping() {
		if (this._isTyping && this.state.activeConversationId) {
			this._isTyping = false;
			if (this.options.onTyping) {
				this.options.onTyping(this.state.activeConversationId, false);
			}
		}
		if (this._typingTimeout) {
			clearTimeout(this._typingTimeout);
			this._typingTimeout = null;
		}
	}

	_showTypingIndicator(userName) {
		if (this._typingIndicator) {
			this._typingIndicator.style.display = 'flex';
			const textEl = this._typingIndicator.querySelector(
				'.liveChat-typing-text'
			);
			if (textEl) {
				textEl.textContent = `${userName || 'Support'} is typing...`;
			}
			this._scrollToBottom();
		}
	}

	_hideTypingIndicator() {
		if (this._typingIndicator) {
			this._typingIndicator.style.display = 'none';
		}
	}

	destroy() {
		if (this._unsubscribe) {
			this._unsubscribe();
		}
		if (this._typingTimeout) {
			clearTimeout(this._typingTimeout);
		}
		this._stopTyping();
		if (this._emojiOutsideHandler) {
			document.removeEventListener('click', this._emojiOutsideHandler);
		}
		if (this._menuOutsideHandler) {
			document.removeEventListener('click', this._menuOutsideHandler);
		}
		if (this.element && this.element.parentNode) {
			this.element.parentNode.removeChild(this.element);
		}
	}
}
